import React, { useEffect, useState } from "react";
import { X, Plus, Trash2, Tag, Users, Mail, Bot, Palette, Image as ImageIcon, Crown, ShieldCheck, UserCircle2, Eye, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { LABEL_PALETTE } from "../lib/colors";

const TABS = [
  { id: "labels", label: "Label", icon: Tag },
  { id: "members", label: "Anggota", icon: Users },
  { id: "background", label: "Latar", icon: Palette },
  { id: "automations", label: "Otomasi", icon: Bot },
];

const ROLE_LABELS = {
  master: { label: "MASTER", icon: Crown, color: "bg-black text-white" },
  spv: { label: "SPV", icon: ShieldCheck, color: "bg-klein text-white" },
  member: { label: "MEMBER", icon: UserCircle2, color: "bg-canary text-black" },
  viewer: { label: "VIEWER", icon: Eye, color: "bg-mist text-black border" },
};

const BG_COLORS = ["#FFFFFF", "#F9F9F9", "#000000", "#002FA7", "#FF2A2A", "#FFD700", "#00C853", "#FF6BEB"];
const BG_GRADIENTS = [
  "linear-gradient(135deg, #002FA7, #FF6BEB)",
  "linear-gradient(135deg, #000000, #002FA7)",
  "linear-gradient(135deg, #FFD700, #FF2A2A)",
  "linear-gradient(135deg, #00C853, #002FA7)",
  "linear-gradient(135deg, #FF6BEB, #FFD700)",
  "linear-gradient(180deg, #F9F9F9, #FFFFFF)",
];

export default function BoardSettings({ board, labels, members, lists, myRole, onClose, onChanged }) {
  const [tab, setTab] = useState("labels");
  const canManage = myRole === "spv" || myRole === "master";

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-2 md:p-4 animate-fadeIn" data-testid="board-settings-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-white border-2 border-black shadow-brutLg max-h-[94dvh] md:max-h-[92vh] flex flex-col animate-blockIn">
        <div className="border-b-2 border-black p-4 md:p-5 bg-black text-white flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-60">[ PAPAN / SETTINGS ]</div>
            <h2 className="font-heading font-black text-2xl uppercase tracking-tight">Pengaturan</h2>
          </div>
          <button onClick={onClose} className="w-10 h-10 shrink-0 border border-white hover:bg-white hover:text-black"><X size={16} className="mx-auto" strokeWidth={2.5} /></button>
        </div>
        <div className="border-b-2 border-black grid grid-cols-2 md:flex md:flex-wrap">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                className={`min-w-0 flex items-center justify-center md:justify-start gap-2 px-3 md:px-4 py-3 font-mono text-[11px] md:text-xs uppercase tracking-widest btn-brut border-r border-b md:border-b-0 border-black ${tab === t.id ? "bg-canary" : "bg-white hover:bg-[#F0F0F0]"}`}>
                <Icon size={12} strokeWidth={2.5} /> {t.label}
              </button>
            );
          })}
        </div>
        <div className="flex-1 overflow-y-auto p-3 md:p-5">
          {tab === "labels" && <LabelsTab boardId={board.board_id} labels={labels} canManage={canManage} onChanged={onChanged} />}
          {tab === "members" && <MembersTab boardId={board.board_id} members={members} canManage={canManage} onChanged={onChanged} myRole={myRole} />}
          {tab === "background" && <BackgroundTab board={board} canManage={canManage} onChanged={onChanged} />}
          {tab === "automations" && <AutomationsTab boardId={board.board_id} lists={lists} labels={labels} members={members} canManage={canManage} />}
        </div>
      </div>
    </div>
  );
}

function LabelsTab({ boardId, labels: initial, canManage, onChanged }) {
  const [labels, setLabels] = useState(initial);
  const [name, setName] = useState("");
  const [color, setColor] = useState(LABEL_PALETTE[0]);

  useEffect(() => setLabels(initial), [initial]);

  const create = async () => {
    if (!name.trim()) return;
    try {
      const r = await api.post(`/boards/${boardId}/labels`, { name: name.trim(), color });
      setLabels((l) => [...l, r.data]);
      setName("");
      onChanged();
    } catch { toast.error("Gagal"); }
  };

  const update = async (label_id, patch) => {
    try {
      const r = await api.patch(`/labels/${label_id}`, patch);
      setLabels((ls) => ls.map((l) => l.label_id === label_id ? r.data : l));
      onChanged();
    } catch { toast.error("Gagal"); }
  };

  const remove = async (label_id) => {
    if (!window.confirm("Hapus label?")) return;
    try {
      await api.delete(`/labels/${label_id}`);
      setLabels((ls) => ls.filter((l) => l.label_id !== label_id));
      onChanged();
    } catch { toast.error("Gagal"); }
  };

  return (
    <div className="space-y-5" data-testid="labels-tab">
      {canManage && (
        <div className="border-2 border-black p-3 md:p-4 space-y-3">
          <div className="font-mono text-[10px] uppercase tracking-widest">Buat Label Baru</div>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-label-name"
            placeholder="Nama label (mis. Urgent, Backend, Riset)"
            className="w-full bg-white border-2 border-black px-3 py-2 font-mono text-sm focus:shadow-brutBlue transition-all" />
          <div className="grid grid-cols-4 sm:flex sm:flex-wrap gap-2">
            {LABEL_PALETTE.map((c) => (
              <button key={c} onClick={() => setColor(c)} data-testid={`palette-${c}`}
                className={`w-8 h-8 border-2 ${color === c ? "border-black shadow-brut -translate-x-0.5 -translate-y-0.5" : "border-black/20"}`}
                style={{ background: c }} />
            ))}
          </div>
          <button onClick={create} data-testid="create-label-btn"
            className="bg-klein text-white border-2 border-black px-4 py-2 font-mono uppercase text-xs tracking-wider btn-brut hover:bg-black hover:-translate-y-0.5 hover:shadow-brut">
            + Tambah Label
          </button>
        </div>
      )}

      <div className="space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-black/60">Label Tersimpan ({labels.length})</div>
        {labels.map((l) => (
          <div key={l.label_id} className="grid grid-cols-[1fr_auto_auto] md:flex md:items-center gap-2 md:gap-3 border-2 border-black p-3" data-testid={`label-row-${l.label_id}`}>
            <span className="col-span-3 md:col-span-1 md:shrink-0 min-w-0 truncate font-mono text-[10px] uppercase tracking-widest px-2 py-1 border-2 border-black"
              style={{ background: l.color, color: getContrast(l.color) }}>{l.name}</span>
            <input value={l.name} onChange={(e) => update(l.label_id, { name: e.target.value })}
              className="col-span-1 md:flex-1 min-w-0 bg-transparent font-bold text-sm border-b border-black/20 focus:border-black"
              disabled={!canManage} />
            {canManage && (
              <>
                <input type="color" value={l.color} onChange={(e) => update(l.label_id, { color: e.target.value })}
                  className="w-10 h-10 border-2 border-black cursor-pointer" />
                <button onClick={() => remove(l.label_id)}
                  className="w-10 h-10 md:w-9 md:h-9 border-2 border-black bg-white hover:bg-signal hover:text-white">
                  <Trash2 size={12} className="mx-auto" strokeWidth={2.5} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MembersTab({ boardId, members, canManage, onChanged, myRole }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [submitting, setSubmitting] = useState(false);

  const invite = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post(`/boards/${boardId}/invite`, { email: email.trim(), role });
      if (r.data.status === "added") toast.success("Pengguna ditambahkan");
      else if (r.data.status === "pending") toast.success("Undangan akan aktif saat login");
      else toast.info("Sudah anggota");
      setEmail("");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setSubmitting(false); }
  };

  const updateRole = async (uid, newRole) => {
    try { await api.patch(`/boards/${boardId}/members/${uid}`, { role: newRole }); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const remove = async (uid) => {
    if (!window.confirm("Keluarkan dari papan?")) return;
    try { await api.delete(`/boards/${boardId}/members/${uid}`); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="space-y-5" data-testid="members-tab">
      {canManage && (
        <form onSubmit={invite} className="border-2 border-black p-3 md:p-4 space-y-3">
          <div className="font-mono text-[10px] uppercase tracking-widest">Undang ke Papan</div>
          <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="board-invite-email"
              placeholder="email@contoh.com"
              className="w-full sm:flex-1 sm:min-w-[200px] bg-white border-2 border-black px-3 py-2 font-mono text-sm focus:shadow-brutBlue transition-all" />
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="board-invite-role"
              className="w-full sm:w-auto bg-white border-2 border-black px-3 py-2 font-mono text-xs uppercase tracking-wider">
              <option value="member">MEMBER</option>
              <option value="spv">SPV</option>
              <option value="viewer">VIEWER</option>
            </select>
            <button type="submit" disabled={submitting} data-testid="board-invite-submit"
              className="w-full sm:w-auto bg-klein text-white border-2 border-black px-4 py-2 font-mono uppercase text-xs tracking-wider btn-brut hover:bg-black hover:-translate-y-0.5 hover:shadow-brut">
              <Mail size={12} className="inline mr-1" strokeWidth={2.5} /> Undang
            </button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-black/60">Anggota Papan ({members.length})</div>
        {members.map((m) => {
          const r = ROLE_LABELS[m.role] || ROLE_LABELS.member;
          return (
            <div key={m.user_id} className="flex flex-wrap md:flex-nowrap items-center gap-3 border-2 border-black p-3" data-testid={`member-row-${m.user_id}`}>
              <div className="shrink-0"><Avatar user={m.user} size={36} /></div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{m.user?.name}</div>
                <div className="font-mono text-[10px] text-black/60 truncate">{m.user?.email}</div>
              </div>
              {canManage && m.role !== "master" ? (
                <select value={m.role} onChange={(e) => updateRole(m.user_id, e.target.value)}
                  className="w-full md:w-auto bg-white border-2 border-black px-2 py-2 md:py-1 font-mono text-[10px] uppercase tracking-wider">
                  <option value="spv">SPV</option>
                  <option value="member">MEMBER</option>
                  <option value="viewer">VIEWER</option>
                </select>
              ) : (
                <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border-2 border-black ${r.color}`}>
                  {r.label}
                </span>
              )}
              {canManage && m.role !== "master" && (
                <button onClick={() => remove(m.user_id)} className="w-10 h-10 md:w-9 md:h-9 border-2 border-black bg-white hover:bg-signal hover:text-white">
                  <Trash2 size={12} className="mx-auto" strokeWidth={2.5} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BackgroundTab({ board, canManage, onChanged }) {
  const apply = async (type, value) => {
    try {
      await api.patch(`/boards/${board.board_id}`, { background_type: type, background_value: value });
      onChanged();
      toast.success("Latar diperbarui");
    } catch { toast.error("Gagal"); }
  };

  const uploadBg = async (file) => {
    if (!file) return;
    // For background we'll piggyback on attachment endpoint via a temporary card? Simpler: just data-URL but that's huge.
    // Use a placeholder card "_bg_" or just upload to imgbb directly via backend new endpoint? We'll add one.
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Use the attachments endpoint pointing at a synthetic flow: we'll reuse same backend by uploading and grabbing URL
      // Workaround: create a hidden card-less endpoint would be nice, but here we use the first attachment of any card.
      // Simpler approach: encode and send to a new dedicated endpoint -> but we didn't add it. Use direct ImgBB via backend not available.
      // For MVP we'll re-use the upload pipeline by posting to a special path that returns just URL. Add quick endpoint instead.
      const r = await api.post(`/boards/${board.board_id}/background-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      apply("image", r.data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal upload");
    }
  };

  return (
    <div className="space-y-6" data-testid="background-tab">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest mb-2">Warna Solid</div>
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
          {BG_COLORS.map((c) => (
            <button key={c} disabled={!canManage} onClick={() => apply("color", c)} data-testid={`bg-color-${c}`}
              className="h-14 border-2 border-black btn-brut hover:-translate-y-0.5 hover:shadow-brut"
              style={{ background: c }} />
          ))}
        </div>
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest mb-2">Gradient</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {BG_GRADIENTS.map((g, i) => (
            <button key={i} disabled={!canManage} onClick={() => apply("gradient", g)} data-testid={`bg-grad-${i}`}
              className="h-16 border-2 border-black btn-brut hover:-translate-y-0.5 hover:shadow-brut"
              style={{ background: g }} />
          ))}
        </div>
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest mb-2">Gambar Latar</div>
        {canManage ? (
          <label className="block border-2 border-dashed border-black p-6 text-center cursor-pointer hover:bg-canary transition-colors">
            <input type="file" accept="image/*" hidden onChange={(e) => uploadBg(e.target.files?.[0])} data-testid="bg-upload-input" />
            <ImageIcon size={28} strokeWidth={1.5} className="mx-auto mb-2" />
            <div className="font-mono text-xs uppercase tracking-widest">Klik untuk unggah gambar</div>
          </label>
        ) : (
          <div className="font-mono text-xs text-black/50">Hanya SPV+ yang bisa ubah latar.</div>
        )}
      </div>
      {canManage && (
        <button onClick={() => apply(null, null)} data-testid="bg-reset-btn"
          className="bg-white border-2 border-black px-4 py-2 font-mono uppercase text-xs tracking-wider btn-brut hover:bg-black hover:text-white">
          Reset ke Default
        </button>
      )}
    </div>
  );
}

function AutomationsTab({ boardId, lists, labels, members, canManage }) {
  const [autos, setAutos] = useState([]);
  const [creating, setCreating] = useState(false);
  const [accessibleBoards, setAccessibleBoards] = useState([]);
  const [form, setForm] = useState({
    name: "",
    trigger_type: "card.moved_to_list",
    trigger_list: "",
    trigger_label: "",
    trigger_actors: [],   // user_ids; empty = any member
    action_type: "add_label",
    action_target_board: "",
    action_target_list: "",
    action_label: "",
  });

  const load = async () => {
    try { const r = await api.get(`/boards/${boardId}/automations`); setAutos(r.data); } catch {}
    try { const r = await api.get(`/my/accessible-lists`); setAccessibleBoards(r.data); } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Isi nama"); return; }
    let trigger_config = {};
    if (form.trigger_type === "card.moved_to_list" || form.trigger_type === "card.created_in_list") {
      if (form.trigger_list) trigger_config.list_id = form.trigger_list;
    } else if (form.trigger_type === "comment.created") {
      if (form.trigger_actors.length > 0) trigger_config.actor_user_ids = form.trigger_actors;
    } else if (form.trigger_type === "label.added_to_card") {
      if (form.trigger_label) trigger_config.label_id = form.trigger_label;
      if (form.trigger_actors.length > 0) trigger_config.actor_user_ids = form.trigger_actors;
    }
    let action_config = {};
    if (form.action_type === "add_label") action_config.label_id = form.action_label;
    if (form.action_type === "move_card_to_list" || form.action_type === "move_card_to_board") {
      action_config.target_list_id = form.action_target_list;
      if (form.action_target_board) action_config.target_board_id = form.action_target_board;
    }
    try {
      const r = await api.post(`/boards/${boardId}/automations`, {
        name: form.name.trim(),
        trigger_type: form.trigger_type, trigger_config,
        action_type: form.action_type, action_config,
      });
      setAutos((a) => [...a, r.data]);
      setCreating(false);
      setForm({ name: "", trigger_type: "card.moved_to_list", trigger_list: "", trigger_label: "", trigger_actors: [], action_type: "add_label", action_target_board: "", action_target_list: "", action_label: "" });
      toast.success("Otomasi dibuat");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus otomasi?")) return;
    try { await api.delete(`/automations/${id}`); setAutos((a) => a.filter((x) => x.automation_id !== id)); } catch {}
  };

  const toggleActor = (uid) => {
    setForm((f) => ({
      ...f,
      trigger_actors: f.trigger_actors.includes(uid) ? f.trigger_actors.filter((x) => x !== uid) : [...f.trigger_actors, uid],
    }));
  };

  const targetBoardLists = accessibleBoards.find((b) => b.board_id === form.action_target_board)?.lists || [];

  const describeTrigger = (a) => {
    const tc = a.trigger_config || {};
    const lst = lists.find((l) => l.list_id === tc.list_id);
    const lbl = labels.find((l) => l.label_id === tc.label_id);
    const actorNames = (tc.actor_user_ids || []).map((uid) => members.find((m) => m.user_id === uid)?.user?.name).filter(Boolean).join(", ");
    if (a.trigger_type === "card.moved_to_list") return `Kartu dipindah ke ${lst?.title || "(daftar apa saja)"}`;
    if (a.trigger_type === "card.created_in_list") return `Kartu baru di ${lst?.title || "(daftar apa saja)"}`;
    if (a.trigger_type === "comment.created") return `Komentar dibuat oleh ${actorNames || "siapa saja"}`;
    if (a.trigger_type === "label.added_to_card") return `Label "${lbl?.name || "?"}" ditambahkan oleh ${actorNames || "siapa saja"}`;
    return a.trigger_type;
  };
  const describeAction = (a) => {
    const ac = a.action_config || {};
    const lst = lists.find((l) => l.list_id === ac.target_list_id);
    const otherList = accessibleBoards.flatMap((b) => b.lists).find((l) => l.list_id === ac.target_list_id);
    const otherBoard = accessibleBoards.find((b) => b.board_id === ac.target_board_id);
    const lbl = labels.find((l) => l.label_id === ac.label_id);
    if (a.action_type === "move_card_to_list") return `Pindah ke ${lst?.title || "?"}`;
    if (a.action_type === "move_card_to_board") return `Pindah ke ${otherBoard?.board_title || "?"} → ${otherList?.title || "?"}`;
    if (a.action_type === "add_label") return `Tambah label "${lbl?.name || "?"}"`;
    if (a.action_type === "archive_card") return "Arsipkan kartu";
    return a.action_type;
  };

  return (
    <div className="space-y-4" data-testid="automations-tab">
      <div className="font-mono text-xs text-black/70">
        Otomasi <span className="bg-canary px-1 font-bold">JIKA X → MAKA Y</span>. 4 jenis trigger, aksi lintas-papan.
      </div>
      {canManage && (
        <>
          {!creating ? (
            <button onClick={() => setCreating(true)} data-testid="open-automation-form"
              className="bg-klein text-white border-2 border-black px-4 py-2 font-mono uppercase text-xs tracking-wider btn-brut hover:bg-black hover:-translate-y-0.5 hover:shadow-brut">
              + Buat Otomasi
            </button>
          ) : (
            <div className="border-2 border-black p-4 space-y-3 bg-[#F9F9F9]" data-testid="automation-form">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nama otomasi" data-testid="automation-name"
                className="w-full bg-white border-2 border-black px-3 py-2 font-mono text-sm" />

              {/* TRIGGER */}
              <div className="space-y-2">
                <div className="font-mono text-[10px] uppercase tracking-widest">JIKA (Trigger)</div>
                <select value={form.trigger_type} onChange={(e) => setForm({ ...form, trigger_type: e.target.value })}
                  data-testid="automation-trigger-type"
                  className="w-full bg-white border-2 border-black px-2 py-2 font-mono text-xs uppercase">
                  <option value="card.moved_to_list">Kartu dipindah ke daftar</option>
                  <option value="card.created_in_list">Kartu baru di daftar</option>
                  <option value="comment.created">Komentar dibuat</option>
                  <option value="label.added_to_card">Label ditambahkan ke kartu</option>
                </select>
                {(form.trigger_type === "card.moved_to_list" || form.trigger_type === "card.created_in_list") && (
                  <select value={form.trigger_list} onChange={(e) => setForm({ ...form, trigger_list: e.target.value })}
                    data-testid="automation-trigger-list"
                    className="w-full bg-white border-2 border-black px-2 py-2 font-mono text-xs uppercase">
                    <option value="">— daftar apa saja —</option>
                    {lists.map((l) => <option key={l.list_id} value={l.list_id}>{l.title}</option>)}
                  </select>
                )}
                {form.trigger_type === "label.added_to_card" && (
                  <select value={form.trigger_label} onChange={(e) => setForm({ ...form, trigger_label: e.target.value })}
                    data-testid="automation-trigger-label"
                    className="w-full bg-white border-2 border-black px-2 py-2 font-mono text-xs uppercase">
                    <option value="">— label apa saja —</option>
                    {labels.map((l) => <option key={l.label_id} value={l.label_id}>{l.name}</option>)}
                  </select>
                )}
                {(form.trigger_type === "comment.created" || form.trigger_type === "label.added_to_card") && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest mb-1 text-black/60">Oleh anggota (kosong = siapa saja)</div>
                    <div className="flex flex-wrap gap-1">
                      {members.map((m) => {
                        const active = form.trigger_actors.includes(m.user_id);
                        return (
                          <button key={m.user_id} type="button" onClick={() => toggleActor(m.user_id)}
                            data-testid={`actor-${m.user_id}`}
                            className={`px-2 py-1 border-2 font-mono text-[10px] uppercase ${active ? "bg-klein text-white border-black" : "bg-white border-black/30 hover:border-black"}`}>
                            {m.user?.name || m.user?.email}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* ACTION */}
              <div className="space-y-2 pt-2 border-t border-black/20">
                <div className="font-mono text-[10px] uppercase tracking-widest">MAKA (Action)</div>
                <select value={form.action_type} onChange={(e) => setForm({ ...form, action_type: e.target.value, action_target_list: "", action_label: "" })}
                  data-testid="automation-action-type"
                  className="w-full bg-white border-2 border-black px-2 py-2 font-mono text-xs uppercase">
                  <option value="add_label">Tambah label di kartu</option>
                  <option value="move_card_to_list">Pindah ke daftar (papan ini)</option>
                  <option value="move_card_to_board">Pindah ke papan lain → daftar</option>
                  <option value="archive_card">Arsipkan kartu</option>
                </select>
                {form.action_type === "add_label" && (
                  <select value={form.action_label} onChange={(e) => setForm({ ...form, action_label: e.target.value })}
                    className="w-full bg-white border-2 border-black px-2 py-2 font-mono text-xs uppercase">
                    <option value="">Pilih label…</option>
                    {labels.map((l) => <option key={l.label_id} value={l.label_id}>{l.name}</option>)}
                  </select>
                )}
                {form.action_type === "move_card_to_list" && (
                  <select value={form.action_target_list} onChange={(e) => setForm({ ...form, action_target_list: e.target.value })}
                    className="w-full bg-white border-2 border-black px-2 py-2 font-mono text-xs uppercase">
                    <option value="">Pilih daftar…</option>
                    {lists.map((l) => <option key={l.list_id} value={l.list_id}>{l.title}</option>)}
                  </select>
                )}
                {form.action_type === "move_card_to_board" && (
                  <>
                    <select value={form.action_target_board} onChange={(e) => setForm({ ...form, action_target_board: e.target.value, action_target_list: "" })}
                      className="w-full bg-white border-2 border-black px-2 py-2 font-mono text-xs uppercase">
                      <option value="">Pilih papan tujuan…</option>
                      {accessibleBoards.filter((b) => b.board_id !== boardId).map((b) => (<option key={b.board_id} value={b.board_id}>{b.board_title}</option>))}
                    </select>
                    {form.action_target_board && (
                      <select value={form.action_target_list} onChange={(e) => setForm({ ...form, action_target_list: e.target.value })}
                        className="w-full bg-white border-2 border-black px-2 py-2 font-mono text-xs uppercase">
                        <option value="">Pilih daftar tujuan…</option>
                        {targetBoardLists.map((l) => (<option key={l.list_id} value={l.list_id}>{l.title}</option>))}
                      </select>
                    )}
                  </>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 md:flex md:justify-end pt-3 border-t border-black/20">
                <button onClick={() => setCreating(false)} className="bg-white border-2 border-black px-3 py-2 font-mono uppercase text-xs">Batal</button>
                <button onClick={submit} data-testid="submit-automation"
                  className="bg-klein text-white border-2 border-black px-4 py-2 font-mono uppercase text-xs tracking-wider btn-brut hover:bg-black">Simpan</button>
              </div>
            </div>
          )}
        </>
      )}
      <div className="space-y-2">
        {autos.length === 0 && <div className="font-mono text-xs text-black/50 text-center py-4">Belum ada otomasi.</div>}
        {autos.map((a) => (
          <div key={a.automation_id} className="border-2 border-black p-3 flex items-start gap-3" data-testid={`automation-${a.automation_id}`}>
            <Bot size={20} strokeWidth={2.5} className="mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">{a.name}</div>
              <div className="font-mono text-[11px] text-black/60 mt-1">
                <span className="bg-canary px-1.5 py-0.5 mr-1">JIKA</span> {describeTrigger(a)}
              </div>
              <div className="font-mono text-[11px] text-black/60 mt-0.5">
                <span className="bg-klein text-white px-1.5 py-0.5 mr-1">MAKA</span> {describeAction(a)}
              </div>
            </div>
            {canManage && (
              <button onClick={() => remove(a.automation_id)} className="w-10 h-10 md:w-9 md:h-9 shrink-0 border-2 border-black bg-white hover:bg-signal hover:text-white">
                <Trash2 size={12} className="mx-auto" strokeWidth={2.5} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Avatar({ user, size = 32 }) {
  if (!user) return null;
  return user.picture ? (
    <img src={user.picture} alt="" style={{ width: size, height: size }} className="border-2 border-black object-cover" />
  ) : (
    <div style={{ width: size, height: size }} className="bg-canary border-2 border-black flex items-center justify-center font-mono text-xs font-bold">
      {(user.name || "?").slice(0, 2).toUpperCase()}
    </div>
  );
}

function getContrast(hex) {
  if (!hex) return "#000";
  const c = hex.replace("#", "");
  const r = parseInt(c.slice(0, 2), 16), g = parseInt(c.slice(2, 4), 16), b = parseInt(c.slice(4, 6), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) > 170 ? "#000" : "#fff";
}
